// /admin/templates/:templateId (architecture sprint-11 §9.5). Every available project-level key; "Reset"
// unsets it (projects created from the template then inherit it). Supervisor capability rows included.
import { isSettingKey } from '@shared/settings-catalogue';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { templateItem } from '@/components/settings/setting-items';
import { SettingGroups } from '@/components/settings/SettingGroups';
import { Skeleton } from '@/components/ui/skeleton';
import { useSetTemplateValue, useTemplate, useUnsetTemplateValue } from '@/hooks/use-templates';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { SettingModeValue } from '@/types/settings';

export function TemplateDetailPage(): JSX.Element {
  const { templateId = '' } = useParams();
  const detail = useTemplate(templateId);
  const set = useSetTemplateValue(templateId);
  const unset = useUnsetTemplateValue(templateId);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  if (detail.isError) return <NotFoundPage />;
  if (!detail.data)
    return (
      <Page width="3xl">
        <Skeleton className="h-64 w-full" />
      </Page>
    );

  const items = detail.data.items.map(templateItem);
  const modeOf = (key: string): SettingModeValue | undefined => detail.data?.items.find((i) => i.key === key)?.mode ?? undefined;

  function save(key: string, value: unknown, mode?: SettingModeValue): void {
    if (!isSettingKey(key)) return;
    setPendingKey(key);
    const keep = mode ?? modeOf(key);
    set.mutate({ key, value, ...(keep ? { mode: keep } : {}) }, { onSettled: () => setPendingKey(null) });
  }

  return (
    <Page width="3xl">
      <p className="mb-2 text-xs">
        <Link to="/admin/templates" className="text-link hover:underline">
          Templates
        </Link>
      </p>
      <PageHeader title={detail.data.template.name} hint="New projects copy these values. Keys that are not set inherit the global value." />
      <SettingGroups
        items={items}
        context="template"
        pendingKey={pendingKey}
        onChange={(key, value) => save(key, value)}
        onModeChange={(key, mode, value) => save(key, value, mode)}
        onReset={(key) => {
          if (!isSettingKey(key)) return;
          setPendingKey(key);
          unset.mutate(key, { onSettled: () => setPendingKey(null) });
        }}
      />
    </Page>
  );
}
